import type { IntlConfig } from 'react-intl';
import type { LocaleHint, Supported } from '../hint';
import { generateAliases } from '../src/match';

export const CURRENT_LOCALES = ['en-US', 'ja-JP'] as const;

export const GATED_LOCALES = [
  'de-DE',
  'es-ES',
  'fr-FR',
  'it-IT',
  'ko-KR',
  'pt-BR',
  'ru-RU',
  'zh-CN',
] as const;

/**
 * Type of supported locales in the application.
 */
export const LOCALES = [...CURRENT_LOCALES, ...GATED_LOCALES, 'zz-ZZ'] as const;
export const locales = LOCALES[Symbol.iterator].bind(LOCALES);

export type LocaleId = (typeof LOCALES)[number];

export type LocaleInfo = {
  id: LocaleId;
  name: string;
};

/** Map of supported locales to their display names. */
export const LOCALE_INFO = {
  'en-US': { id: 'en-US', name: 'English (US)' },
  'ja-JP': { id: 'ja-JP', name: '日本語' },
  'de-DE': { id: 'de-DE', name: 'Deutsch' },
  'es-ES': { id: 'es-ES', name: 'Español' },
  'fr-FR': { id: 'fr-FR', name: 'Français' },
  'it-IT': { id: 'it-IT', name: 'Italiano' },
  'ko-KR': { id: 'ko-KR', name: '한국어' },
  'pt-BR': { id: 'pt-BR', name: 'Português (Brasil)' },
  'ru-RU': { id: 'ru-RU', name: 'Русский' },
  'zh-CN': { id: 'zh-CN', name: '简体中文' },
  'zz-ZZ': { id: 'zz-ZZ', name: 'zz_ZZ Test Locale' },
} as const satisfies Record<LocaleId, LocaleInfo>;

/**
 * Default locale for the application.
 * Used as fallback when user's preferred locale is not available.
 */
export const DEFAULT_LOCALE: LocaleId = 'en-US';

/**
 * Return the valid locale for a given specifier.
 *
 * @param specifier
 * @returns
 */
export function localeFor(
  specifier: string | undefined | null
): LocaleId | undefined {
  if (specifier == null) {
    return undefined;
  }
  return ALIASES[normalize(specifier)];
}

/**
 * Alias a specifier to a locale.
 *
 * The specifier must be a non-empty string. The locale must be a valid LocaleId.
 *
 * @param specifier
 * @param locale
 * @returns
 */
export function alias(
  specifier: string | undefined | null,
  locale: LocaleId
): LocaleId | undefined {
  if (!specifier) {
    return undefined;
  }
  const norm = normalize(specifier);
  // eslint-disable-next-line no-return-assign
  return ALIASES[norm] ?? (ALIASES[norm] = locale);
}

export const ALIASES: Record<string, LocaleId> = {};

for (const [specifier, locale] of generateAliases(LOCALES)) {
  alias(specifier, locale);
}

function normalize(specifier: string): string {
  return specifier.toLowerCase().replaceAll('_', '-');
}

export async function loadLocale(locale: LocaleId): Promise<IntlConfig>;
export async function loadLocale(
  hint: LocaleHint & Supported
): Promise<IntlConfig>;
export async function loadLocale(
  spec: string | LocaleHint
): Promise<IntlConfig | null>;
export async function loadLocale(
  spec: string | LocaleHint
): Promise<IntlConfig | null> {
  const locale = typeof spec === 'string' ? localeFor(spec) : spec.locale;
  if (!locale) {
    return null;
  }
  return {
    locale,
    messages: await import(`./generated/${locale}.json`).then((m) => m.default),
  };
}
